/**
 * The Excel of "My office network" (CU-869fmjzrh D11, CA 22): the rows of the current view — every office, collapsed
 * groups included — as arrays with a header, so the file has the screen's figures by construction. Two sheets:
 * "Distance to market" (one row per office × column) and "Prices by provider" (one row per provider × office × column).
 * The server only writes them. Pure and alias-free.
 */

export const SHEETS = Object.freeze({ DISTANCE: 'Distance to market', PRICES: 'Prices by provider' })
const TYPE = { airport: 'Airport', downtown: 'City', train_station: 'Train station', truck: 'Trucks', other: 'Other' }
const REASON = {
	noPrices: 'No prices', noYou: 'No prices of yours', fewCompetitors: 'Not enough competitors',
	notCaptured: 'No prices captured', noWebsite: 'No website of yours in this country',
}
const isNum = v => typeof v === 'number' && Number.isFinite(v)
const r2 = v => (isNum(v) ? Math.round(v * 100) / 100 : null)
const pct = v => (isNum(v) ? Math.round(v * 1000) / 10 : null)
const label = c => c?.excelLabel || c?.label || ''

/** `{ name, header, rows }` of the first sheet. `model` from `buildNetworkModel` (lengths or pick-up view). */
export function distanceSheet(model) {
	const market = model.mode === 'market'
	const header = market
		? ['Office', 'Country', 'Office type', 'Column', 'Market median', 'Providers', 'Median change %', 'Prices', 'Note']
		: ['Office', 'Country', 'Office type', 'Column', 'Your price', 'Market median', 'Competitors', 'Distance €', 'Distance %',
			'Position', 'Of', 'Cheapest', 'Cheapest price', 'Change (pt)', 'Your prices', 'Market prices', 'Note']
	const rows = []
	for (const row of model.rows) {
		row.cells.forEach((cell, j) => {
			const base = [row.office.name, row.office.countryName || row.office.country || '', TYPE[row.office.type] || '', label(model.columns[j])]
			const note = REASON[row.reason] || REASON[cell.reason] || (cell.thin ? 'Few prices' : null)
			if (market) {
				rows.push([...base, r2(cell.median), cell.nCompetitors, pct(cell.marketChange), cell.n || 0, note])
				return
			}
			const yourN = cell.you?.n || 0
			rows.push([...base, r2(cell.you?.v ?? null), r2(cell.median), cell.nCompetitors, r2(cell.distanceE), pct(cell.distance),
				cell.position, cell.you ? cell.total : null, cell.cheapest?.name || null, r2(cell.cheapest?.v ?? null), r2(cell.change),
				yourN, Math.max((cell.n || 0) - yourN, 0), note])
		})
	}
	return { name: SHEETS.DISTANCE, header, rows }
}

/** The second sheet: every price behind every cell, You and yours marked */
export function pricesSheet(model) {
	const header = ['Office', 'Country', 'Column', model.mode === 'site' ? 'Website' : 'Provider', 'Yours', 'Price', 'Prices']
	const rows = []
	for (const row of model.rows) {
		row.cells.forEach((cell, j) => {
			for (const e of cell.list) {
				const mine = cell.you && e.id === cell.you.id ? 'You' : cell.yours.some(y => y.id === e.id) ? 'Yours' : null
				rows.push([row.office.name, row.office.countryName || row.office.country || '', label(model.columns[j]), e.name, mine, r2(e.v), e.n || 0])
			}
		})
	}
	return { name: SHEETS.PRICES, header, rows }
}

/** The body of `POST /api/dashboard/office-network/excel`: the view (for the usage log), the file name parts, the sheets */
export function excelBody(model, { storedView, view, captureTo }) {
	return { view: storedView, file: { view, captureTo }, sheets: [distanceSheet(model), pricesSheet(model)] }
}
